import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useServicesStore } from '../stores/servicesStore';
import { slugify } from '../lib/slug';
import { useIcon } from '../lib/icons.js';
import { Search } from 'lucide-react';
import SEO from '../components/SEO';
import { generateStructuredData } from '../lib/seoStructuredData';

// Small icon renderer for category icons (library key or image URL)
function CategoryIcon({ iconKey, name, size = 16 }) {
  const DefaultComp = useIcon('md:MdCategory');
  const IconComp = useIcon(iconKey);
  const [imgError, setImgError] = useState(false);
  const isUrl = (v) => typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('./') || v.startsWith('../'));
  if (IconComp) return <IconComp size={size} />;
  if (isUrl(iconKey) && !imgError) {
    return (
      <img src={iconKey} alt={`${name || 'Service category'} icon`} className="w-4 h-4 object-contain" onError={() => setImgError(true)} />
    );
  }
  if (DefaultComp) return <DefaultComp size={size} />;
  return null;
}

export default function Services() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Use central store for categories and items (for counts)
  const items = useServicesStore((s) => s.items);
  const categoriesList = useServicesStore((s) => s.categoriesList);
  const categoryMap = useServicesStore((s) => s.categoryMap);
  const fetchCategories = useServicesStore((s) => s.fetchCategories);
  const resetFilters = useServicesStore((s) => s.resetFilters);
  const initLoad = useServicesStore((s) => s.initLoad);

  useEffect(() => {
    fetchCategories();
    initLoad();
  }, [fetchCategories, initLoad]);

  // Build parent → children tree similar to AllServices
  const catTree = useMemo(() => {
    const list = Array.isArray(categoriesList) ? categoriesList : [];
    const parents = [];
    const byParent = new Map();
    list.forEach((c) => {
      const pid = c.parent_id || c.parentId || null;
      if (pid) {
        if (!byParent.has(pid)) byParent.set(pid, []);
        byParent.get(pid).push(c);
      } else {
        parents.push(c);
      }
    });
    // Sort parents by display_order (nulls last), then name
    parents.sort((a, b) => {
      const orderA = a.display_order ?? 9999;
      const orderB = b.display_order ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || '').localeCompare(b.name || '');
    });
    return parents.map((p) => {
      const pName = (p.name || p.title || '').trim();
      const kidsRaw = (Array.isArray(p.subcategories) ? p.subcategories : (byParent.get(p.id) || []));
      const kids = (kidsRaw || []).filter((sc) => {
        const scName = (sc.name || sc.title || '').trim();
        return scName && scName !== pName; // avoid duplicate child
      });
      // Sort children by display_order (nulls last), then name
      kids.sort((a, b) => {
        const orderA = a.display_order ?? 9999;
        const orderB = b.display_order ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
      });
      return { parent: p, children: kids };
    });
  }, [categoriesList]);

  // Category counts from items for display (best-effort)
  const categoryCounts = useMemo(() => {
    const counts = {};
    (Array.isArray(items) ? items : []).forEach((s) => {
      const name = (s?.category_id && categoryMap?.[s.category_id]) || s?.category || s?.subcategory || s?.mainCategory || '';
      if (!name) return;
      const k = String(name);
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [items, categoryMap]);

  // Filter groups by search across parent and children names
  const displayGroups = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const groups = Array.isArray(catTree) ? catTree.map((g) => {
      const pn = (g?.parent?.name || g?.parent?.title || '').trim();
      const children = Array.isArray(g.children) ? g.children : [];
      const matches = (pn && pn.toLowerCase().includes(q)) || children.some((sc) => String(sc.name || sc.title || '').toLowerCase().includes(q));
      return { ...g, matches };
    }) : [];
    return groups.filter((g) => g.matches);
  }, [catTree, search]);

  return (
    <>
      <SEO
        title="Creative Services | Nordic Freelancers & Professionals - Kreativ"
        description="Browse thousands of creative services from talented Nordic freelancers. Find designers, developers, writers, and creative professionals across Sweden, Norway, Denmark, Finland, and Iceland."
        keywords="creative services, freelance services, nordic freelancers, design services, development services, creative professionals, sweden, norway, denmark, finland, iceland"
        image="/logo-kreativ-light.png"
        type="website"
        structuredData={generateStructuredData.serviceList(items)}
      />
      <div className="container mx-auto px-4 py-6">
      {/* Hero */}
      <div className="px-6 py-10 mb-8 text-center">
        <h1 className="text-2xl md:text-3xl font-bold mb-3 text-primary-text">
          {t('services.header', 'Find the perfect service for your needs')}
        </h1>
        <p className="text-sm text-secondary-text mb-4">
          {t('services.description', 'Browse through thousands of services offered by talented freelancers')}
        </p>
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-text" size={18} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const q = String(search || '').trim();
                  const path = q ? `/services/allservices/q/${slugify(q)}` : '/services/allservices';
                  resetFilters();
                  navigate(path);
                }
              }}
              placeholder={t('services.searchPlaceholder', 'Search for services')}
              className="w-full h-11 rounded-lg bg-input-bg border border-card-border pl-10 pr-3 text-sm text-primary-text"
            />
          </div>
          <Link
            to={(() => { const q = String(search || '').trim(); return q ? `/services/allservices/q/${slugify(q)}` : '/services/allservices'; })()}
            onClick={() => resetFilters()}
            className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-2"
            data-variant="primary"
          >
            {t('services.viewAll', { defaultValue: 'View All Services' })}
          </Link>
        </div>
      </div>

      {/* Section header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-primary-text">{t('services.ourServices', 'Our Services')}</h2>
        <p className="text-sm text-secondary-text">
          {t('services.ourServicesDescription', 'Browse through thousands of services offered by talented freelancers')}
        </p>
      </div>

      {/* Parent categories and children */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(displayGroups) && displayGroups.map(({ parent, children }) => {
          const pName = (parent?.name || parent?.title || '').trim();
          const parentCount = categoryCounts[pName] || 0;
          const childCounts = (Array.isArray(children) ? children : []).reduce((sum, sc) => {
            const scName = (sc?.name || sc?.title || '').trim();
            return sum + (categoryCounts[scName] || 0);
          }, 0);
          const totalCount = parentCount + childCounts;
          const iconKey = parent?.icon_url || parent?.icon;
          return (
            <div key={parent?.id || pName} className="rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {iconKey ? <CategoryIcon iconKey={iconKey} name={pName} size={18} /> : null}
                  <Link
                    to={`/services/allservices/category/${slugify(pName)}`}
                    className="font-semibold text-base md:text-lg hover:underline text-primary-text"
                  >
                    {pName}
                  </Link>
                  <span className="text-xs text-secondary-text">{totalCount} {t('services.countSuffix', 'services')}</span>
                </div>
              </div>
              <ul className="space-y-2 pl-4 ml-2 border-l border-card-border">
                {Array.isArray(children) && children.map((sc) => {
                  const scName = (sc?.name || sc?.title || '').trim();
                  if (!scName || scName === pName) return null;
                  const cnt = categoryCounts[scName] || 0;
                  const scIconKey = sc?.icon_url || sc?.icon;
                  return (
                    <li key={sc?.id || scName} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        {scIconKey ? <CategoryIcon iconKey={scIconKey} name={scName} size={16} /> : null}
                        <Link
                          to={`/services/allservices/category/${slugify(scName)}`}
                          className="truncate hover:underline text-sm text-secondary-text"
                        >
                          {scName}
                        </Link>
                      </div>
                      <span className="text-xs text-secondary-text">{cnt}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
      </div>
    </>
  );
}
